// Descender-aware text block height measurement.
//
// Fabric's Textbox.height = numLines * fontSize * lineHeight. That omits the
// portion of descenders (g, j, p, q, y) that hang below the baseline of the
// final line. When we emit the persisted height into the export overlay with
// `overflow: hidden`, the descender on the last line gets clipped.
//
// This helper computes a height that includes the real descender using
// TextMetrics.fontBoundingBoxDescent (falling back to actualBoundingBoxDescent,
// then to the line-box formula). It never returns less than Fabric's value.

let sharedCtx: CanvasRenderingContext2D | null = null;

function getCtx(): CanvasRenderingContext2D | null {
  if (sharedCtx) return sharedCtx;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  sharedCtx = c.getContext('2d');
  return sharedCtx;
}

export interface MeasureInput {
  lines: string[];
  fontSize: number;
  fontFamily: string;
  fontWeight?: number | string;
  fontStyle?: string;
  lineHeight?: number;
}

export interface MeasuredHeight {
  height: number;
  descenderPad: number;
  method: 'fontBoundingBox' | 'actualBoundingBox' | 'lineBox';
}

export function measureTextBlockHeight(input: MeasureInput): MeasuredHeight {
  const {
    lines,
    fontSize,
    fontFamily,
    fontWeight = 400,
    fontStyle = 'normal',
    lineHeight = 1.16,
  } = input;

  const numLines = Math.max(1, lines.length || 1);
  const lineBox = numLines * fontSize * lineHeight;

  const ctx = getCtx();
  if (!ctx) {
    return { height: Math.ceil(lineBox), descenderPad: 0, method: 'lineBox' };
  }

  const fontSpec = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`;
  try {
    ctx.font = fontSpec;
  } catch {
    return { height: Math.ceil(lineBox), descenderPad: 0, method: 'lineBox' };
  }

  // Measure the last line (the one whose descender can hang below the box)
  // and a synthetic descender probe, then take the max. Using 'gjpqy' ensures
  // we get a descent value even when the actual text has no descenders but we
  // still want a consistent box height for the face.
  const sample = lines[numLines - 1] || '';
  const descentProbe = 'gjpqy';

  let ascent = fontSize * 0.8;
  let descent = fontSize * 0.2;
  let method: MeasuredHeight['method'] = 'lineBox';

  try {
    const probe = ctx.measureText(descentProbe);
    const actual = ctx.measureText(sample);

    const fbAsc = (probe as TextMetrics & { fontBoundingBoxAscent?: number }).fontBoundingBoxAscent;
    const fbDesc = (probe as TextMetrics & { fontBoundingBoxDescent?: number }).fontBoundingBoxDescent;

    if (typeof fbAsc === 'number' && typeof fbDesc === 'number' && fbAsc > 0 && fbDesc > 0) {
      ascent = fbAsc;
      descent = fbDesc;
      method = 'fontBoundingBox';
    } else {
      const aAsc = Math.max(
        (actual as TextMetrics & { actualBoundingBoxAscent?: number }).actualBoundingBoxAscent || 0,
        (probe as TextMetrics & { actualBoundingBoxAscent?: number }).actualBoundingBoxAscent || 0
      );
      const aDesc = Math.max(
        (actual as TextMetrics & { actualBoundingBoxDescent?: number }).actualBoundingBoxDescent || 0,
        (probe as TextMetrics & { actualBoundingBoxDescent?: number }).actualBoundingBoxDescent || 0
      );
      if (aAsc > 0 && aDesc > 0) {
        ascent = aAsc;
        descent = aDesc;
        method = 'actualBoundingBox';
      }
    }
  } catch {
    // fall through to lineBox default
  }

  // Interior line spacing from line-box, plus the real ascent+descent on the
  // last line so the bottom-most descender sits inside the box.
  const metricsHeight = ascent + descent + (numLines - 1) * fontSize * lineHeight;
  const finalHeight = Math.ceil(Math.max(lineBox, metricsHeight));
  const descenderPad = Math.max(0, finalHeight - Math.ceil(lineBox));

  return { height: finalHeight, descenderPad, method };
}

// Convenience for single-string inputs (splits on newline; wrapping must be
// pre-resolved by the caller via Fabric's textLines).
export function measureTextBlockHeightFromString(
  text: string,
  fontSize: number,
  fontFamily: string,
  fontWeight: number | string = 400,
  fontStyle: string = 'normal',
  lineHeight: number = 1.16
): MeasuredHeight {
  const lines = (text || '').split('\n');
  return measureTextBlockHeight({
    lines,
    fontSize,
    fontFamily,
    fontWeight,
    fontStyle,
    lineHeight,
  });
}
